import React from 'react'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Typography,
  Box
} from '@mui/material'

// Shared column definition for trawl-station rows. Exported so the map-level
// "Export Displayed (CSV)" results bar can reuse it without redefining the
// column list — keep this in sync with the rendered table below.
export const TRAWL_STATION_COLUMNS = [
  { label: 'SEAMAP Station', key: 'stationid' },
  { label: 'Start Depth (m)', key: 'depth_ssta' },
  { label: 'End Depth (m)', key: 'depth_esta' },
  { label: 'Date', key: 'mo_day_yr' },
  { label: 'Gears', key: 'gears' },
  { label: 'Surface Temp (C)', key: 'temp_ssurf' },
  { label: 'Bottom Temp (C)', key: 'temp_bot' },
  { label: 'Air Temp (C)', key: 'temp_sair' },
  { label: 'Baro Pressure (mmHg)', key: 'b_prssr' },
  { label: 'Wind Speed (kts)', key: 'wind_spd' },
  { label: 'Wind Direction', key: 'wind_dir' },
  { label: 'Wave Height (m)', key: 'wave_ht' },
  { label: 'Valid Swept Area', key: 'valid_swept_area' },
  { label: 'Tow Distance (nmi)', key: 'tow_distance_nmi' },
  { label: 'Effective Width (ft)', key: 'gear_width_ft' },
  { label: 'Swept Area (hectares)', key: 'swept_area_hectares' },
  { label: 'Swept Area Method', key: 'swept_area_method' },
  { label: 'Swept Area QA', key: 'swept_area_warning' },
  { label: 'Vessel', key: 'vessel' },
  { label: 'Cruise', key: 'cruise' },
  { label: 'State', key: 'state' }
]

const HIDDEN_DETAIL_KEYS = new Set(['swept_area_method', 'swept_area_warning'])
const TRAWL_STATION_DETAIL_COLUMNS = TRAWL_STATION_COLUMNS.filter(col => !HIDDEN_DETAIL_KEYS.has(col.key))

// Normalize a raw row into export shape (collapse mo/day/yr → mo_day_yr).
// Shared with the results bar so the CSV from the bar matches the CSV from
// the per-point detail button byte-for-byte.
export const formatTrawlRowForExport = (row) => {
  const next = { ...row }
  if (next.mo && next.day && next.yr && !next.mo_day_yr) {
    next.mo_day_yr = `${next.mo}/${next.day}/${next.yr}`
  }
  return next
}

// `data` is the currently selected station shown in the detail table.
// The per-point CSV button was removed in favor of the always-visible
// ResultsExportBar at the top of the table panel; that bar covers all
// displayed rows, so a per-point button here was redundant.
export default function PointDataTableTrawl({ data }) {
  if (!data) {
    return null
  }

  const columns = TRAWL_STATION_DETAIL_COLUMNS

  // Format the date if it exists
  if (data.mo && data.day && data.yr) {
    data.mo_day_yr = `${data.mo}/${data.day}/${data.yr}`
  }

  return (
    <Box sx={{ 
      width: '100%', 
      paddingLeft: { xs: '0.5rem', sm: '1rem' },
      paddingRight: { xs: '0.5rem', sm: '1rem' }
    }}>
      <Box sx={{ marginBottom: { xs: 1, sm: 2 } }}>
        <Typography variant="h6" sx={{ 
          color: '#2E5A88', 
          fontSize: { xs: '0.9rem', sm: '1rem' } 
        }}>
          Trawl Station Details
        </Typography>
      </Box>
      <TableContainer 
        component={Paper} 
        elevation={2}
        sx={{ 
          width: '100%',
          overflowX: 'auto', // Enable horizontal scrolling
          overflowY: 'visible',
          maxHeight: 'none' // Remove height restrictions
        }}
      >
        <Table sx={{ 
          width: '100%',
          minWidth: { xs: '800px', md: '100%' } // Minimum width for mobile scrolling
        }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #ccc',
                    color: '#1a2a3a',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '8px', sm: '16px' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } }}>
                {columns.map((col) => {
                    let cellValue = data[col.key];

                    if (typeof cellValue === 'boolean') {
                      cellValue = cellValue ? 'Yes' : 'No';
                    }

                    // Check if the value is numeric (or can be converted to a number)
                    if (!isNaN(cellValue) && cellValue !== null && cellValue !== '' && typeof cellValue !== 'boolean') {
                      // Determine formatting based on column key
                      if (col.key === 'b_prssr' || col.key === 'wind_dir') {
                        // Format as integer with no decimal places
                        cellValue = Number(cellValue).toFixed(0);
                      } else if (col.key === 'swept_area_hectares') {
                        cellValue = Number(cellValue).toFixed(2);
                      } else if (col.key === 'tow_distance_nmi' || col.key === 'gear_width_ft') {
                        cellValue = Number(cellValue).toFixed(2);
                      } else {
                        // Format as number with one decimal place
                        cellValue = Number(cellValue).toFixed(1);
                      }
                    }

                    // Format vessel and cruise keys as strings with no decimal places
                    if (col.key === 'vessel' || col.key === 'cruise' || col.key === 'stationid') {
                      // Ensure the value is treated as a string
                      cellValue = String(cellValue).split('.')[0];
                    }

                    return (
                      <TableCell key={col.key} sx={{ 
                        color: '#333333',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px', sm: '16px' },
                        whiteSpace: 'nowrap'
                      }}>
                        {cellValue}
                      </TableCell>
                    );
                })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
